import React, { useEffect, useState } from "react";
import { CarouselItemProps } from "./types";
import { StyleSheet, TouchableOpacity } from "react-native";
import { Image as ExpoImage } from "expo-image";
import { View } from "moti";
import {
  getImageSource,
  getIOSImageProps,
  getOptimizedImageUrl,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import EmptyImagePreview from "./EmptyImage/EmptyImagePreview";
import { VDBIcons } from "@utils/VDBIcons";
import { MediaType } from "@utils/constants";
import TooltipButton from "@scenes/pdp/utils/TooltipButton";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { DefaultTheme } from "styled-components";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { isPair } from "@utils/product/productValidators";
import { getShapeValue } from "./EmptyImage/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import CertificateIcon from "@components/atoms/CertificateIcon";

const CarouselItem = ({
  testID,
  item,
  itemDetail,
  index,
  activeIndex,
  image_thumb_url,
  changeActiveIndex,
  thumbColor,
  thumbSize,
  thumbResizeMode,
  thumbDefaultBorderColor,
  iconSize,
}: CarouselItemProps) => {
  const [imageError, setImageError] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const three_d_settings = useSelector(
    (state: any) => state.globalData?.app_settings?.three_d
  );
  const { disableImageZoom } = useUserInfo();

  let imageUri = "";
  if (
    item.type === MediaType.IMAGE ||
    item.type === MediaType.VIDEO ||
    (item.type === "pdf" && item.thumb)
  ) {
    imageUri = item.thumb ? item.thumb : image_thumb_url;
  }

  useEffect(() => {
    if (
      item.type === MediaType.IMAGE ||
      item.type === MediaType.VIDEO ||
      (item.type === "pdf" && item.thumb)
    ) {
      if (imageUri === "" || imageUri === undefined) {
        setImageError(true);
      } else {
        setImageError(false);
      }
    }
  }, [item.type, item.thumb, image_thumb_url, imageUri]);

  const handleError = () => {
    setImageError(true);
  };

  return (
    <>
      <TouchableOpacity
        testID={testID}
        onPress={() => changeActiveIndex(index)}
        activeOpacity={0.8}
      >
        <View>
          {item.type === MediaType.IMAGE ||
          item.type === MediaType.VIDEO ||
          (item.type === "pdf" && item.thumb) ? (
            !imageError ? (
              <ExpoImage
                contentFit={
                  disableImageZoom()
                    ? "contain"
                    : (thumbResizeMode as "cover" | "contain" | "fill")
                }
                source={getImageSource(
                  getOptimizedImageUrl(imageUri, ImageSizePreset.PDP_THUMB),
                  ImageSizePreset.PDP_THUMB
                )}
                {...getIOSImageProps()}
                onError={handleError}
                style={[
                  styles.thumb,
                  {
                    borderColor:
                      activeIndex === index
                        ? thumbColor
                        : thumbDefaultBorderColor,
                    backgroundColor: theme["core-01"],
                  },
                  { width: thumbSize, height: thumbSize },
                ]}
              />
            ) : (
              <View
                style={[
                  styles.thumb,
                  {
                    borderColor:
                      activeIndex === index
                        ? thumbColor
                        : thumbDefaultBorderColor,
                  },
                  { width: thumbSize, height: thumbSize },
                ]}
              >
                <EmptyImagePreview
                  shape={getShapeValue(itemDetail)}
                  size={40}
                  isPair={isPair(itemDetail) ?? false}
                />
              </View>
            )
          ) : item.type === MediaType.MODEL_3D ? (
            theme.isLargeScreen ? (
              <TooltipButton
                children={
                  <View
                    style={[
                      styles.thumb,
                      {
                        borderColor:
                          activeIndex === index
                            ? thumbColor
                            : thumbDefaultBorderColor,
                        backgroundColor: theme["core-01"],
                        justifyContent: "center",
                        alignContent: "center",
                        alignItems: "center",
                      },
                      { width: thumbSize, height: thumbSize },
                    ]}
                  >
                    <VDBIcon
                      name={three_d_settings?.icon?.data || VDBIcons.THREE_D}
                      uri={three_d_settings?.icon?.data}
                      size={iconSize ? iconSize : 45}
                      thumbSize={thumbSize}
                      color={theme["core-06"]}
                      variant={
                        three_d_settings?.icon?.type === IconsType.img_url
                          ? IconsVariant.CustomUrl
                          : IconsVariant.VDBCustom
                      }
                    />
                  </View>
                }
                width={"auto"}
                enableTooltip={true}
                alignSelfFlexStart={true}
                customZIndex={1000000000}
                renderComponent={
                  <TooltipContainer>
                    <VDBText
                      type={typography["Minor-R"]}
                      color={theme["core-01"]}
                    >
                      {three_d_settings?.tooltip_text
                        ? three_d_settings?.tooltip_text ?? ""
                        : t(
                            "itemDetail.threeD.tooltip.text",
                            "3D View Available"
                          )}
                    </VDBText>
                  </TooltipContainer>
                }
              />
            ) : (
              <View
                style={[
                  styles.thumb,
                  {
                    borderColor:
                      activeIndex === index
                        ? thumbColor
                        : thumbDefaultBorderColor,
                    backgroundColor: theme["core-01"],
                    justifyContent: "center",
                    alignContent: "center",
                    alignItems: "center",
                  },
                  { width: thumbSize, height: thumbSize },
                ]}
              >
                <VDBIcon
                  name={three_d_settings?.icon?.data || VDBIcons.THREE_D}
                  uri={three_d_settings?.icon?.data}
                  size={iconSize ? iconSize : 45}
                  thumbSize={thumbSize}
                  color={theme["core-06"]}
                  variant={
                    three_d_settings?.icon?.type === IconsType.img_url
                      ? IconsVariant.CustomUrl
                      : IconsVariant.VDBCustom
                  }
                />
              </View>
            )
          ) : (
            <View
              style={[
                styles.thumb,
                {
                  borderColor:
                    activeIndex === index
                      ? thumbColor
                      : thumbDefaultBorderColor,
                  backgroundColor: theme["core-01"],
                  justifyContent: "center",
                  alignContent: "center",
                  alignItems: "center",
                },
                { width: thumbSize, height: thumbSize },
              ]}
            >
              <CertificateIcon
                mediaType={item.type}
                labId={itemDetail?.lab}
                size={50}
                color={theme["core-06"]}
              />
            </View>
          )}
          {item.type === MediaType.VIDEO && (
            <VideoContainer thumbSize={thumbSize}>
              <VDBIcon
                name={"play"}
                size={iconSize ? iconSize : 24}
                color={theme["core-01"]}
                variant={IconsVariant.VDBCustom}
              />
            </VideoContainer>
          )}
        </View>
      </TouchableOpacity>
    </>
  );
};

const styles = StyleSheet.create({
  header: {
    position: "absolute",
    top: 0,
    width: "100%",
  },
  footer: {
    bottom: 0,
    position: "absolute",
    width: "100%",
  },
  activeThumb: {
    borderWidth: 3,
  },
  thumb: {
    borderRadius: 8,
    borderWidth: 1,
  },
  thumbnailListContainer: {
    paddingHorizontal: 10,
  },
  bottomFlatlist: {
    position: "absolute",
  },
});

const VideoContainer = styled.View<any>`
  border-radius: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? "8px" : "12px"};
  position: absolute;
  align-items: center;
  justify-content: center;
  height: ${({ thumbSize }: { thumbSize: number }) => thumbSize}px;
  width: ${({ thumbSize }: { thumbSize: number }) => thumbSize}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["over-02"]};
`;

const TooltipContainer = styled.View`
  padding: ${SpaceType.ExtraSmall}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-06"]};
`;

export default React.memo(CarouselItem);
